import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
  Box,
} from '@mui/material';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import ErrorIcon from '@mui/icons-material/Error';
import { useTheme } from '@mui/material/styles';

const ConfirmDialog = ({
  open,
  title,
  message,
  confirmText = 'Confirm',
  cancelText = 'Cancel',
  onConfirm,
  onCancel,
  type = 'warning', // 'warning' or 'error'
  loading = false,
}) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';

  const Icon = type === 'error' ? ErrorIcon : WarningAmberIcon;
  const confirmBgColor = type === 'error' 
    ? (isDark ? 'rgba(239, 68, 68, 0.15)' : 'rgba(239, 68, 68, 0.1)')
    : (isDark ? 'rgba(245, 158, 11, 0.15)' : 'rgba(245, 158, 11, 0.1)');

  return (
    <Dialog
      open={open}
      onClose={onCancel}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          backgroundColor: isDark ? '#1a1a1a' : '#ffffff',
          backgroundImage: 'none',
          border: isDark ? '1px solid rgba(255, 255, 255, 0.05)' : 'none',
        },
      }}
    >
      <DialogTitle
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
          pb: 2,
          borderBottom: isDark ? '1px solid rgba(255, 255, 255, 0.05)' : '1px solid #e2e8f0',
          color: isDark ? '#ffffff' : '#1a202c',
          fontWeight: 600,
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: 40,
            height: 40,
            borderRadius: '50%',
            backgroundColor: confirmBgColor,
          }}
        >
          <Icon 
            sx={{
              fontSize: 20,
              color: type === 'error' ? '#ef4444' : '#f59e0b',
            }}
          />
        </Box>
        {title}
      </DialogTitle>
      <DialogContent sx={{ pt: 3 }}>
        <DialogContentText
          sx={{
            color: isDark ? '#cbd5e1' : '#4a5568',
            fontSize: '0.95rem',
            lineHeight: 1.6,
          }}
        >
          {message}
        </DialogContentText>
      </DialogContent>
      <DialogActions
        sx={{
          gap: 1,
          p: 2,
          borderTop: isDark ? '1px solid rgba(255, 255, 255, 0.05)' : '1px solid #e2e8f0',
        }}
      >
        <Button
          onClick={onCancel}
          disabled={loading}
          sx={{
            color: isDark ? '#cbd5e1' : '#718096',
            backgroundColor: isDark ? 'rgba(255, 255, 255, 0.05)' : '#f7fafc',
            border: isDark ? '1px solid rgba(255, 255, 255, 0.1)' : '1px solid #e2e8f0',
            '&:hover': {
              backgroundColor: isDark ? 'rgba(255, 255, 255, 0.08)' : '#edf2f7',
            },
            textTransform: 'none',
            fontWeight: 500,
          }}
        >
          {cancelText}
        </Button>
        <Button
          onClick={onConfirm}
          disabled={loading}
          variant="contained"
          sx={{
            backgroundColor: type === 'error' ? '#ef4444' : '#f59e0b',
            color: '#ffffff',
            textTransform: 'none',
            fontWeight: 600,
            '&:hover': {
              backgroundColor: type === 'error' ? '#dc2626' : '#d97706',
              boxShadow: isDark
                ? '0 4px 12px rgba(0, 0, 0, 0.5)'
                : '0 4px 12px rgba(0, 0, 0, 0.15)',
            },
            '&:disabled': {
              backgroundColor: isDark ? 'rgba(255, 255, 255, 0.2)' : '#cbd5e1',
              color: isDark ? 'rgba(255, 255, 255, 0.5)' : '#a0aec0',
            },
          }}
        >
          {loading ? 'Processing...' : confirmText}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ConfirmDialog;
